import { useEffect, useState } from 'react';
import { useCamera } from '../lib/use-camera';
import { useQrScanner } from '../lib/use-qr-scanner';

interface QrScannerViewProps {
  /** Callback wenn ein QR-Code erkannt wurde. */
  onScan: (value: string) => void;
  /** Ob der Scanner aktiv ist. */
  isActive: boolean;
}

/**
 * Kamera-View mit QR-Code Scan-Overlay.
 * Zeigt Kamera-Feed mit Ziel-Rahmen und manuellem Eingabefeld als Fallback.
 *
 * @param props - Komponenten-Props
 * @returns QR-Scanner Ansicht mit Kamera oder Fehler-Anzeige
 *
 * @example
 * ```tsx
 * <QrScannerView isActive={true} onScan={(userId) => handleScan(userId)} />
 * ```
 */
export function QrScannerView({ onScan, isActive }: QrScannerViewProps) {
  const { videoRef, permissionStatus, errorMessage, startCamera, stopCamera } = useCamera();
  const { scannerStatus, scannerError, lastScannedValue } = useQrScanner({
    videoRef,
    onScan,
    isActive: isActive && permissionStatus === 'granted',
  });
  const [manualInput, setManualInput] = useState('');

  useEffect(() => {
    if (isActive) {
      void startCamera();
    } else {
      stopCamera();
    }
  }, [isActive, startCamera, stopCamera]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = manualInput.trim();
    if (trimmed) {
      onScan(trimmed);
      setManualInput('');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        width: '100%',
        backgroundColor: '#0f0f23',
        color: '#ffffff',
      }}
    >
      {/* Camera Feed */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '480px',
          aspectRatio: '1',
          backgroundColor: '#1a1a2e',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {permissionStatus === 'granted' && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {/* QR Targeting Overlay */}
        {permissionStatus === 'granted' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: '60%',
                height: '60%',
                border: `3px solid ${lastScannedValue ? '#86efac' : '#4ade80'}`,
                borderRadius: '8px',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)',
                transition: 'border-color 0.15s ease',
                // Kurzer "Flash" bei Erkennung
                ...(lastScannedValue && {
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.4), 0 0 20px rgba(134,239,172,0.6)',
                }),
              }}
            />
          </div>
        )}

        {/* Permission States */}
        {permissionStatus === 'idle' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <button
              onClick={() => void startCamera()}
              style={{
                minHeight: '48px',
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#4ade80',
                color: '#0f0f23',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              Kamera starten
            </button>
          </div>
        )}

        {permissionStatus === 'requesting' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#aaa',
            }}
          >
            Kamera wird geoeffnet...
          </div>
        )}

        {(permissionStatus === 'denied' || permissionStatus === 'error') && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '16px',
              gap: '12px',
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: '32px' }}>X</span>
            <p style={{ color: '#f87171', margin: 0, fontSize: '14px' }}>{errorMessage}</p>
          </div>
        )}
      </div>

      {scannerStatus === 'error' && scannerError && (
        <p style={{ color: '#f87171', fontSize: '13px', textAlign: 'center', margin: 0 }}>
          QR-Erkennung: {scannerError}
        </p>
      )}

      {/* Manual Entry Fallback */}
      <div style={{ width: '100%', maxWidth: '480px' }}>
        <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '8px', textAlign: 'center' }}>
          Oder Nutzer-ID manuell eingeben:
        </p>
        <form onSubmit={handleManualSubmit} style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            placeholder="Nutzer-ID eingeben..."
            style={{
              flex: 1,
              minHeight: '48px',
              padding: '12px 16px',
              fontSize: '16px',
              backgroundColor: '#1a1a2e',
              color: '#ffffff',
              border: '1px solid #333',
              borderRadius: '8px',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            style={{
              minHeight: '48px',
              padding: '12px 16px',
              fontSize: '16px',
              backgroundColor: '#4ade80',
              color: '#0f0f23',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          >
            OK
          </button>
        </form>
      </div>
    </div>
  );
}
